// src/pages/Visitors/parts/VisitorFormModal.tsx
//
// Check-in / edit form for the Visitor Log.
//   create → POST /visitors/check-in   (multipart/form-data)
//   edit   → PUT  /visitors/:id        (multipart/form-data)
//
// Both handlers call ParseMultipartForm on the backend, so both requests go
// out as FormData — a JSON body is rejected before any field is read.
//
// The ID proof photo is optional on both endpoints. In edit mode the preview
// starts as the stored CDN URL; `photoDirty` tracks whether the user actually
// chose a new file, so an unchanged photo is never re-uploaded (which would
// also make the backend delete the old R2 object for nothing).

import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import {
  ID_PROOF_OPTIONS,
  MEET_PERSON_TYPE_OPTIONS,
  PURPOSE_OPTIONS,
  VISITOR_TYPE_OPTIONS,
  VisitorRecord,
} from './types';

interface VisitorFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  /** Required in edit mode. */
  initialRecord?: VisitorRecord | null;
  onClose: () => void;
  onSuccess: () => void;
}

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

const emptyForm = {
  visitorName: '',
  visitorType: '',
  contactNo: '',
  idProofType: '',
  purpose: '',
  purposeDetails: '',
  meetPersonType: '',
  meetPersonName: '',
};

const VisitorFormModal: React.FC<VisitorFormModalProps> = ({
  open,
  mode,
  initialRecord,
  onClose,
  onSuccess,
}) => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrlRef = useRef<string | null>(null);

  const [form, setForm] = useState(emptyForm);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [photoDirty, setPhotoDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const isCreate = mode === 'create';

  const revokePreview = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) return;
    const r = initialRecord;
    revokePreview();
    setForm({
      visitorName: r?.visitorName ?? '',
      visitorType: r?.visitorType ?? '',
      contactNo: r?.contactNo ?? '',
      idProofType: r?.idProofType ?? '',
      purpose: r?.purpose ?? '',
      purposeDetails: r?.purposeDetails ?? '',
      meetPersonType: r?.meetPersonType ?? '',
      meetPersonName: r?.meetPersonName ?? '',
    });
    setPhotoFile(null);
    setPhotoPreview(r?.photoUrl ?? '');
    setPhotoDirty(false);
    setSaving(false);
    setTouched(false);
    setIsClosing(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [open, initialRecord]);

  useEffect(() => revokePreview, []);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const setField = (key: keyof typeof emptyForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const missing = {
    visitorName: !form.visitorName.trim(),
    visitorType: !form.visitorType,
    idProofType: !form.idProofType,
    purpose: !form.purpose,
    meetPersonType: !form.meetPersonType,
    meetPersonName: !form.meetPersonName.trim(),
  };
  const canSave = !Object.values(missing).some(Boolean);

  const handleClose = () => {
    if (saving) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Unsupported file',
        detail: 'Choose an image file for the ID proof photo.',
        life: 4000,
      });
      e.target.value = '';
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.current?.show({
        severity: 'warn',
        summary: 'File too large',
        detail: 'Choose an image under 5 MB.',
        life: 4000,
      });
      e.target.value = '';
      return;
    }

    revokePreview();
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setPhotoFile(file);
    setPhotoPreview(url);
    setPhotoDirty(true);
  };

  const clearNewPhoto = () => {
    revokePreview();
    setPhotoFile(null);
    setPhotoDirty(false);
    setPhotoPreview(initialRecord?.photoUrl ?? '');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const buildFormData = (): FormData => {
    const fd = new FormData();
    fd.append('visitor_name', form.visitorName.trim());
    fd.append('visitor_type', form.visitorType);
    fd.append('contact_no', form.contactNo.trim());
    fd.append('id_proof_type', form.idProofType);
    fd.append('purpose', form.purpose);
    fd.append('purpose_details', form.purposeDetails.trim());
    fd.append('meet_person_type', form.meetPersonType);
    fd.append('meet_person_name', form.meetPersonName.trim());
    // Field is still called visitor_photo on the backend, but it holds the
    // ID proof document rather than a face photo.
    if (photoDirty && photoFile) fd.append('visitor_photo', photoFile);
    return fd;
  };

  const handleSave = async () => {
    setTouched(true);
    if (!canSave || saving) return;
    if (!isCreate && !initialRecord) return;

    setSaving(true);
    try {
      const fd = buildFormData();
      let res;

      if (isCreate) {
        // Deny-entry isn't exposed in the UI, so every check-in is checked_in.
        fd.append('status', 'checked_in');
        res = await apiClient.post('api/visitors/check-in', fd);
      } else {
        res = await apiClient.put(`api/visitors/${initialRecord!.id}`, fd);
      }

      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: isCreate ? 'Checked in' : 'Saved',
          detail: isCreate
            ? `${form.visitorName.trim()} is now on premises.`
            : 'Visitor details updated.',
          life: 4000,
        });
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 300);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'The visitor could not be saved.',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong while saving.',
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const showError = (key: keyof typeof missing) => touched && missing[key];

  // ── Pill group (same options UX, styled for this modal) ──────────────────
  const PillGroup: React.FC<{
    options: string[];
    value: string;
    invalid: boolean;
    onChange: (v: string) => void;
  }> = ({ options, value, invalid, onChange }) => (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        padding: invalid ? 8 : 0,
        border: invalid ? '1px solid #fca5a5' : 'none',
        borderRadius: 12,
        background: invalid ? 'rgba(239,68,68,.04)' : 'transparent',
      }}
    >
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => !saving && onChange(opt)}
            className="visitor-pill"
            disabled={saving}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              border: `1.5px solid ${active ? '#3b82f6' : '#e5e7eb'}`,
              background: active
                ? 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)'
                : 'white',
              color: active ? 'white' : '#4b5563',
              boxShadow: active ? '0 4px 12px rgba(59, 130, 246, 0.25)' : 'none',
              transition: 'all .15s',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );

  const SectionHeader: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 16,
        paddingBottom: 12,
        borderBottom: '1px solid rgba(229, 231, 235, 0.6)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: 8,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          flexShrink: 0,
        }}
      >
        <i className={icon} style={{ fontSize: 15 }} />
      </div>
      <span
        style={{
          color: '#1e3a8a',
          fontWeight: 700,
          fontSize: 14,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
        }}
      >
        {title}
      </span>
    </div>
  );

  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <div
      style={{
        fontSize: 13,
        fontWeight: 600,
        color: '#1e3a8a',
        marginBottom: 8,
      }}
    >
      {text}
      {required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
    </div>
  );

  const sectionCardStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.8)',
    borderRadius: 16,
    padding: 20,
    border: '1px solid rgba(59, 130, 246, 0.1)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
      <div
        style={{
          width: 54,
          height: 54,
          borderRadius: 14,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: 24,
          fontWeight: 700,
          boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
          flexShrink: 0,
          border: '3px solid white',
        }}
      >
        <i className={isCreate ? 'pi pi-sign-in' : 'pi pi-user-edit'} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: '#1e40af',
            lineHeight: 1.2,
            marginBottom: 4,
          }}
        >
          {isCreate ? 'Check in a visitor' : 'Edit visitor details'}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {isCreate
            ? 'Record a new visitor entering the premises'
            : 'Update visitor information below'}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: 920, borderRadius: 24, overflow: 'hidden' }}
        contentStyle={{
          padding: 18,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
          borderRight: '12px solid rgba(255, 255, 255, 0.8)',
          borderTop: '12px solid rgba(255, 255, 255, 0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal
        dismissableMask={!saving}
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .p-dialog-mask {
            backdrop-filter: blur(4px);
            background-color: rgba(0, 0, 0, 0.25) !important;
            animation: maskFadeIn 0.35s ease;
          }
          .p-dialog-mask.mask-closing {
            animation: maskFadeOut 0.4s ease !important;
          }
          @keyframes maskFadeIn {
            from { opacity: 0; background-color: rgba(0, 0, 0, 0); }
            to { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
          }
          @keyframes maskFadeOut {
            from { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
            to { opacity: 0; background-color: rgba(0, 0, 0, 0); }
          }
          .p-dialog {
            animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          }
          .p-dialog.dialog-closing {
            animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          @keyframes dialogOpen {
            from { opacity: 0; transform: scale(0.92) translateY(-20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes dialogClose {
            from { opacity: 1; transform: scale(1) translateY(0); }
            to { opacity: 0; transform: scale(0.88) translateY(25px); }
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track {
            background: rgba(241, 245, 249, 0.5);
            border-radius: 10px;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb {
            background: rgba(148, 163, 184, 0.5);
            border-radius: 10px;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover {
            background: rgba(100, 116, 139, 0.7);
          }
          .visitor-pill:hover:not(:disabled) {
            border-color: #93c5fd !important;
          }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* ── ID proof photo ── */}
          <div style={sectionCardStyle}>
            <SectionHeader icon="pi pi-id-card" title="ID Proof Photo" />
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 14, fontWeight: 500 }}>
              Optional. A photo of the ID card the visitor presented. JPG/PNG · Max 5 MB.
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <div
                style={{
                  width: 150,
                  height: 96,
                  borderRadius: 12,
                  overflow: 'hidden',
                  border: photoPreview
                    ? '2px solid rgba(59, 130, 246, 0.3)'
                    : '1.5px dashed #c7d2fe',
                  background: photoPreview
                    ? '#f1f5f9'
                    : 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="ID proof"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <>
                    <i className="pi pi-id-card" style={{ fontSize: 22, color: '#3b82f6', marginBottom: 4 }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>No photo</span>
                  </>
                )}
              </div>

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 160 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 8,
                      border: '1.5px solid #3b82f6',
                      background: 'white',
                      color: '#3b82f6',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      opacity: saving ? 0.6 : 1,
                    }}
                  >
                    <i className="pi pi-upload" style={{ fontSize: 12 }} />
                    {photoPreview ? 'Replace photo' : 'Upload photo'}
                  </button>

                  {photoDirty && (
                    <button
                      type="button"
                      onClick={clearNewPhoto}
                      disabled={saving}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 8,
                        border: '1.5px solid #ef4444',
                        background: 'white',
                        color: '#ef4444',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        opacity: saving ? 0.6 : 1,
                      }}
                    >
                      <i className="pi pi-undo" style={{ fontSize: 12 }} />
                      Undo
                    </button>
                  )}
                </div>

                <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
                  {photoDirty && photoFile ? (
                    <span style={{ color: '#10b981', fontWeight: 600 }}>
                      <i className="pi pi-check-circle" style={{ marginRight: 4 }} />
                      {photoFile.name} ({(photoFile.size / 1024).toFixed(1)} KB) ready to upload
                    </span>
                  ) : !photoDirty && !isCreate && photoPreview ? (
                    'Current photo stays unchanged'
                  ) : (
                    'Optional · image under 5 MB'
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Visitor details ── */}
          <div style={sectionCardStyle}>
            <SectionHeader icon="pi pi-user" title="Visitor Details" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <GlobalInputField
                  label="Visitor name"
                  required
                  value={form.visitorName}
                  onChange={(e) => setField('visitorName', e.target.value)}
                  placeholder="e.g. Rakesh Kumar"
                  error={showError('visitorName') ? 'Visitor name is required' : ''}
                  icon="pi pi-user"
                  disabled={saving}
                />
                <GlobalInputField
                  label="Contact number"
                  value={form.contactNo}
                  onChange={(e) => setField('contactNo', e.target.value)}
                  placeholder="e.g. 98765 43210"
                  icon="pi pi-phone"
                  disabled={saving}
                />
              </div>

              <div>
                <FieldLabel text="Visitor type" required />
                <PillGroup
                  options={VISITOR_TYPE_OPTIONS}
                  value={form.visitorType}
                  invalid={showError('visitorType')}
                  onChange={(v) => setField('visitorType', v)}
                />
              </div>

              <div>
                <FieldLabel text="ID proof type" required />
                <PillGroup
                  options={ID_PROOF_OPTIONS}
                  value={form.idProofType}
                  invalid={showError('idProofType')}
                  onChange={(v) => setField('idProofType', v)}
                />
              </div>
            </div>
          </div>

          {/* ── Visit purpose ── */}
          <div style={sectionCardStyle}>
            <SectionHeader icon="pi pi-briefcase" title="Visit Purpose" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <FieldLabel text="Purpose of visit" required />
                <PillGroup
                  options={PURPOSE_OPTIONS}
                  value={form.purpose}
                  invalid={showError('purpose')}
                  onChange={(v) => setField('purpose', v)}
                />
              </div>

              <GlobalTextArea
                label="Purpose details"
                value={form.purposeDetails}
                onChange={(e) => setField('purposeDetails', e.target.value)}
                placeholder="Any extra context worth recording"
                rows={2}
                autoResize
                disabled={saving}
              />
            </div>
          </div>

          {/* ── Meeting ── */}
          <div style={sectionCardStyle}>
            <SectionHeader icon="pi pi-users" title="Meeting With" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <FieldLabel text="Meeting with" required />
                <PillGroup
                  options={MEET_PERSON_TYPE_OPTIONS}
                  value={form.meetPersonType}
                  invalid={showError('meetPersonType')}
                  onChange={(v) => setField('meetPersonType', v)}
                />
              </div>

              <div style={{ maxWidth: 360 }}>
                <GlobalInputField
                  label="Person's name"
                  required
                  value={form.meetPersonName}
                  onChange={(e) => setField('meetPersonName', e.target.value)}
                  placeholder="e.g. Mrs. Sharma"
                  error={showError('meetPersonName') ? "Person's name is required" : ''}
                  icon="pi pi-user"
                  disabled={saving}
                />
              </div>
            </div>
          </div>

          {touched && !canSave && (
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#b91c1c',
                background: 'rgba(239,68,68,.08)',
                border: '1px solid rgba(239,68,68,.2)',
                borderRadius: 12,
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <i className="pi pi-exclamation-circle" />
              Fill in the required fields before saving.
            </div>
          )}

          {/* Submit */}
          <div style={{ textAlign: 'center', marginTop: 4 }}>
            <GlobalButton
              label={
                saving
                  ? isCreate
                    ? 'Checking in...'
                    : 'Saving...'
                  : isCreate
                    ? 'Check in visitor'
                    : 'Save changes'
              }
              onClick={handleSave}
              variant="primary"
              size="medium"
              disabled={saving}
              loading={saving}
              fullWidth={false}
              icon={isCreate ? 'pi pi-check' : 'pi pi-save'}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default VisitorFormModal;